import React from 'react';
import {
  CalendarToday,
  LocationOn,
  Celebration,
  Group,
  EmojiPeople,
  Public,
  School
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import EventLayout from '../../components/events/EventLayout';
import BackButton from '../../components/events/BackButton';
import EventHero from '../../components/events/EventHero';
import ImageCarousel from '../../components/events/ImageCarousel';
import EventMeta from '../../components/events/EventMeta';
import KeyTakeaways from '../../components/events/KeyTakeaways';
import styled from 'styled-components';

const MessageSection = styled.div`
  margin: 60px 0;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 2rem;
  color: var(--color-text);
  margin-bottom: 32px;
  text-align: center;
`;

const MessageContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  border: 1px solid rgba(0,0,0,0.1);

  &:last-child {
    margin-bottom: 0;
  }
`;

const MessageText = styled.p`
  color: var(--color-text);
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 16px;
  font-family: var(--font-serif);
`;

const MessageAuthor = styled.p`
  color: var(--color-text-light);
  font-family: var(--font-sans);
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
`;

const Creation = () => {
  const eventData = {
    title: "Creation of the Association",
    date: "02/15/2024",
    time: "6:00 PM",
    location: "EPFL Campus",
    category: "Milestone",
    description: "SHALOM - the Israeli Association has been established! We are excited to develop it further and create enriching experiences and opportunities for Israeli students, as well as individuals interested in Israeli culture.",
    media: [
      { src: '/events/creation/creation.png', objectFit: 'cover', objectPosition: 'center' },
    ],
    keyTakeaways: [
      {
        icon: <Celebration style={{ fontSize: '24px' }} />,
        title: 'Official Launch',
        description: 'Successfully established SHALOM as an official EPFL student association.',
        color: '#4F8EF7'
      },
      {
        icon: <Group style={{ fontSize: '24px' }} />,
        title: 'Community Building',
        description: 'Created a platform for Israeli students and culture enthusiasts to connect and collaborate.',
        color: '#9C88FF'
      },
      {
        icon: <Public style={{ fontSize: '24px' }} />,
        title: 'Cultural Bridge',
        description: 'Established a foundation for sharing Israeli culture and innovations within EPFL.',
        color: '#50E3C2'
      },
      {
        icon: <School style={{ fontSize: '24px' }} />,
        title: 'Academic Network',
        description: 'Formed connections between Israeli students and the broader EPFL community.',
        color: '#FF6B9D'
      }
    ],
    messages: [
      {
        text: "From tech startups to shakshuka recipes, we're here to share the vibrant spirit of Israel at EPFL. Join us in creating a home away from home while fostering innovation and cultural exchange!",
        author: "Yshai Dinée-Baumgarten, President"
      },
      {
        text: "Beyond managing numbers, I'm excited to help build bridges between Swiss precision and Israeli innovation. Let's create something extraordinary together!",
        author: "Solal Ben Hamou, Treasurer"
      },
      {
        text: "Ready to transform our WhatsApp group chats into real-world connections! From hummus workshops to tech talks, we'll make every event uniquely memorable.",
        author: "Yanaël Grundland, Communications Manager"
      }
    ]
  };

  const metaItems = [
    {
      icon: <EmojiPeople style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #4F8EF7, #9C88FF)',
      title: 'New Association',
      subtitle: 'EPFL Student Organization'
    },
    {
      icon: <CalendarToday style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #50E3C2, #4DD0E1)',
      title: `${formatDate(eventData.date)} at ${eventData.time}`,
      subtitle: 'Foundation Date'
    },
    {
      icon: <LocationOn style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #FF6B9D, #FF8A65)',
      title: eventData.location,
      subtitle: 'Establishment Location'
    }
  ];

  return (
    <EventLayout>
      <BackButton />
      <EventHero
        category={eventData.category}
        title={eventData.title}
        description={eventData.description}
      >
        <EventMeta items={metaItems} />
      </EventHero>

      <ImageCarousel
        media={eventData.media}
        interval={4000}
      />

      <KeyTakeaways items={eventData.keyTakeaways} />

      <MessageSection>
        <SectionTitle>Founding Members' Messages</SectionTitle>
        {eventData.messages.map((message, index) => (
          <MessageContainer key={index}>
            <MessageText>{message.text}</MessageText>
            <MessageAuthor>- {message.author}</MessageAuthor>
          </MessageContainer>
        ))}
      </MessageSection>
    </EventLayout>
  );
};

export default Creation;